import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthProvider";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";

/** ============================
 *  COLLECTIONS & FMT
 *  ============================
 *  - referral_users: affiliates network
 *  - payouts: affiliate earnings (level 1 = direct, >1 = indirect)
 *  - orders: paid orders; contains instructor-referral payouts
 *            in `instructorReferralDistribution`
 */
const COLL = {
  referralUsers: "referral_users",
  payouts: "payouts",
  orders: "orders",
  affiliateSettlements: "affiliate_settlements",
};

const fmtLKR = new Intl.NumberFormat("en-LK", {
  style: "currency",
  currency: "LKR",
});

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};
const pad2 = (n) => String(n).padStart(2, "0");
const dayKey = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export default function Dashboard() {
  const { user, userData } = useAuth();
  const uid = user?.uid || userData?.uid || "";
  const referralCode = userData?.referralCode || "";

  /* ===== Team (referral_users) ===== */
  const [team, setTeam] = useState({ direct: 0, total: 0 });

  /* ===== Affiliate revenue (buyer-side payouts) ===== */
  const [revenue, setRevenue] = useState({
    total: 0,
    direct: 0,
    bonus: 0,
    courseSales: 0,
    productSales: 0,
  });
  const [history, setHistory] = useState([]); // latest payouts
  const [last90Sales, setLast90Sales] = useState([]); // daily amounts: direct/bonus
  const [directSales, setDirectSales] = useState([]); // latest direct payouts
  const [affSalesCount90, setAffSalesCount90] = useState([]); // daily counts
  const [levelBreakdown, setLevelBreakdown] = useState({}); // {1: amt, 2: amt, ...}
  const [settlementSummary, setSettlementSummary] = useState({
    total: 0,
    submitted: 0,
    confirmed: 0,
    disputed: 0,
    rows: [],
  });

  /* ===== Instructor Referral (instructor-side upline payouts) ===== */
  const [instrRefTotal, setInstrRefTotal] = useState(0);
  const [instrRefLast90, setInstrRefLast90] = useState([]); // {date, amount}
  const [instrRefRows, setInstrRefRows] = useState([]); // latest rows

  /* ---------------- Team (L1 + L2) from referral_users ---------------- */
  useEffect(() => {
    if (!uid) return;

    const unsub = onSnapshot(
      collection(db, COLL.referralUsers),
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const matchesDirect = (row = {}) =>
          String(row.referredByUid || "") === String(uid) ||
          (referralCode &&
            (String(row.referredByReferralCode || "") === String(referralCode) ||
              String(row.referredBy || "") === String(referralCode)));

        const directRows = all.filter(matchesDirect);
        const directIds = new Set(
          directRows.map((row) => String(row.uid || row.id || "")).filter(Boolean),
        );
        const directCodes = new Set(
          directRows
            .map((row) => String(row.referralCode || "").trim())
            .filter(Boolean),
        );

        const isSecondLevel = (row = {}) =>
          directIds.has(String(row.referredByUid || "")) ||
          directCodes.has(String(row.referredByReferralCode || "")) ||
          directCodes.has(String(row.referredBy || ""));

        const secondLevelCount = all.filter((row) => isSecondLevel(row)).length;
        setTeam({ direct: directRows.length, total: directRows.length + secondLevelCount });
      },
      (err) => console.error("referral team stream failed:", err),
    );

    return () => unsub();
  }, [uid, referralCode]);

  /* ---------------- Affiliate revenue (payouts) ---------------- */
  useEffect(() => {
    if (!uid) return;

    const qP = query(collection(db, COLL.payouts), where("userId", "==", uid));
    const unsub = onSnapshot(
      qP,
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const recent = all
          .filter((r) => {
            const t =
              r.createdAt?.toDate?.() || new Date(r.createdAt || Date.now());
            return t >= daysAgo(90);
          })
          .sort((a, b) => {
            const da = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const dbb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return da - dbb;
          });

        let total = 0,
          directAmt = 0,
          bonusAmt = 0,
          courseSales = 0,
          productSales = 0;

        const byDayAmt = new Map(); // {date, direct, bonus}
        const byDayCnt = new Map(); // {date, directCount, indirectCount}
        const levelsMap = {}; // {1: amt, 2: amt, ...}

        recent.forEach((p) => {
          const earn = Number(p.amount || 0);
          const lvl = Number(p.level || 1);
          const d =
            p.createdAt?.toDate?.() || new Date(p.createdAt || Date.now());
          const key = dayKey(d);

          total += earn;
          if (lvl === 1) directAmt += earn;
          else bonusAmt += earn;

          // visual "sales" count proxy
          courseSales += 1;

          const a = byDayAmt.get(key) || { date: key, direct: 0, bonus: 0 };
          if (lvl === 1) a.direct += earn;
          else a.bonus += earn;
          byDayAmt.set(key, a);

          const c = byDayCnt.get(key) || {
            date: key,
            directCount: 0,
            indirectCount: 0,
          };
          if (lvl === 1) c.directCount += 1;
          else c.indirectCount += 1;
          byDayCnt.set(key, c);

          levelsMap[lvl] = (levelsMap[lvl] || 0) + earn;
        });

        setRevenue({
          total,
          direct: directAmt,
          bonus: bonusAmt,
          courseSales,
          productSales,
        });
        setLast90Sales(Array.from(byDayAmt.values()));
        setAffSalesCount90(Array.from(byDayCnt.values()));
        setLevelBreakdown(levelsMap);
      },
      (err) => console.error("payouts stream failed:", err)
    );

    return () => unsub();
  }, [uid]);


/* ---------------- Affiliate settlements ---------------- */
useEffect(() => {
  if (!uid) return;

  const qS = query(
    collection(db, COLL.affiliateSettlements),
    where("affiliateUid", "==", uid)
  );
  const unsub = onSnapshot(
    qS,
    (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(b.monthKey || "").localeCompare(String(a.monthKey || "")));

      const summary = rows.reduce(
        (acc, row) => {
          const amount = Number(row.totalCommission || 0);
          acc.total += amount;
          if (row.status === "submitted") acc.submitted += amount;
          if (row.status === "confirmed") acc.confirmed += amount;
          if (row.status === "disputed") acc.disputed += amount;
          return acc;
        },
        { total: 0, submitted: 0, confirmed: 0, disputed: 0, rows: [] }
      );

      summary.rows = rows.slice(0, 8);
      setSettlementSummary(summary);
    },
    (err) => console.error("affiliate settlements stream failed:", err)
  );

  return () => unsub();
}, [uid]);

  // Latest payouts table (direct + recent)
  useEffect(() => {
    if (!uid) return;
    const qP = query(collection(db, COLL.payouts), where("userId", "==", uid));
    const unsub = onSnapshot(
      qP,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const da = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const dbb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return dbb - da;
          })
          .slice(0, 50);
        setDirectSales(rows.filter((p) => Number(p.level || 1) === 1));
        setHistory(rows.slice(0, 8));
      },
      (err) => console.error("history stream failed:", err)
    );
    return () => unsub();
  }, [uid]);

  /* ---------------- Instructor Referral (package payouts) ---------------- */
  useEffect(() => {
    if (!uid) return;

    const qRef = query(collection(db, COLL.payouts), where("userId", "==", uid));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const all = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((row) =>
            String(row.referralKind || "").toLowerCase() === "instructor" ||
            String(row.sourceType || "").startsWith("package_") ||
            String(row.source || "").startsWith("instructor_package")
          );

        const recent = all
          .filter((row) => {
            const created =
              row.createdAt?.toDate?.() || new Date(row.createdAt || Date.now());
            return created >= daysAgo(90);
          })
          .sort((a, b) => {
            const da = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const dbb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return dbb - da;
          });

        const total = all.reduce((sum, row) => sum + Number(row.amount || 0), 0);
        setInstrRefTotal(total);

        setInstrRefRows(recent.slice(0, 20));

        const amtMap = new Map();
        recent.forEach((row) => {
          const dt = row.createdAt?.toDate?.() || new Date(row.createdAt || Date.now());
          const key = dayKey(dt);

          const a = amtMap.get(key) || { date: key, amount: 0 };
          a.amount += Number(row.amount || 0);
          amtMap.set(key, a);
        });

        setInstrRefLast90(
          Array.from(amtMap.values()).sort((a, b) =>
            a.date.localeCompare(b.date)
          )
        );
      },
      (err) => console.error("instructor referral stream failed:", err)
    );

    return () => unsub();
  }, [uid]);

  // 3-month stack from affiliate revenue amounts
  const last3Months = useMemo(() => {
    const map = new Map(); // "MMM YYYY" -> {month, direct, bonus}
    const monthKey = (d) =>
      d.toLocaleString("en-US", { month: "short" }) + " " + d.getFullYear();
    last90Sales.forEach((row) => {
      const [y, m, dd] = row.date.split("-").map((n) => +n);
      const d = new Date(y, m - 1, dd);
      const key = monthKey(d);
      const prev = map.get(key) || { month: key, direct: 0, bonus: 0 };
      prev.direct += row.direct;
      prev.bonus += row.bonus;
      map.set(key, prev);
    });
    return Array.from(map.values()).slice(-3);
  }, [last90Sales]);

  /* --------- Donut (Commission by Level) data --------- */
  const levelPieData = useMemo(() => {
    const l1 = Number(levelBreakdown[1] || 0);
    const l2 = Number(levelBreakdown[2] || 0);
    const l3 = Number(levelBreakdown[3] || 0);
    const others = Object.entries(levelBreakdown).reduce((t, [k, v]) => {
      const n = Number(k);
      return n > 3 ? t + Number(v || 0) : t;
    }, 0);

    const arr = [];
    if (l1 > 0) arr.push({ name: "L1", value: l1, color: "#4F46E5" }); // indigo-600
    if (l2 > 0) arr.push({ name: "L2", value: l2, color: "#10B981" }); // emerald-500
    if (l3 > 0) arr.push({ name: "L3", value: l3, color: "#F59E0B" }); // amber-500
    if (others > 0) arr.push({ name: "L4+", value: others, color: "#9CA3AF" }); // gray-400
    return arr;
  }, [levelBreakdown]);

  // donut value labels
  const renderValueLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    value,
    index,
  }) => {
    if (!value) return null;
    const RADIAN = Math.PI / 180;
    const r = outerRadius + 12;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    const color = levelPieData[index]?.color || "#111827";
    return (
      <text
        x={x}
        y={y}
        fill={color}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        className="text-xs font-semibold"
      >
        {Math.round(value).toLocaleString("en-LK")}
      </text>
    );
  };

  return (
    <div className="min-h-[90vh] bg-gradient-to-br from-blue-50 via-purple-50 to-blue-100 py-8 md:pt-24">
      <div className="max-w-6xl w-full mx-auto">
        {/* ===== KPIs ===== */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
          <KPI
            color="blue"
            title="Affiliate Revenue (Total)"
            value={fmtLKR.format(revenue.total)}
          />
          <KPI
            color="indigo"
            title="Direct (L1)"
            value={fmtLKR.format(revenue.direct)}
          />
          <KPI
            color="violet"
            title="Indirect (L2+)"
            value={fmtLKR.format(revenue.bonus)}
          />
          <KPI
            color="green"
            title="Instructor Referral"
            value={fmtLKR.format(instrRefTotal)}
          />
          <KPI
            color="emerald"
            title="Settlement Income"
            value={fmtLKR.format(settlementSummary.total)}
          />
          <KPI
            color="blue"
            title="Team (Total / Direct)"
            value={`${team.total} / ${team.direct}`}
          />
        </div>

        {/* ===== Donut (Commission by Level) ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card title="Commission by Level (Last 90 days)">
            <div className="h-64">
              {levelPieData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400">
                  No commission yet.
                </div>
              ) : (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={levelPieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="65%"
                      outerRadius="85%"
                      paddingAngle={2}
                      labelLine
                      label={renderValueLabel}
                      isAnimationActive
                    >
                      {levelPieData.map((d, i) => (
                        <Cell key={d.name + i} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmtLKR.format(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          {/* Sales Split (existing) */}
          <Card title="Sales Split (Last 90 days)">
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart
                  data={[
                    {
                      name: "Sales",
                      Course: revenue.courseSales,
                      Product: revenue.productSales,
                    },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Course" />
                  <Bar dataKey="Product" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* 3 months revenue performance */}
          <Card title="Revenue (Last 3 Months)">
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={last3Months}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(v) => fmtLKR.format(v)} />
                  <Legend />
                  <Bar dataKey="direct" name="Direct" />
                  <Bar dataKey="bonus" name="Indirect" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* ===== Daily affiliate + instructor referral charts ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <Card title="Daily Affiliate Revenue (Last 90 days)">
            <div className="h-56">
              <ResponsiveContainer>
                <LineChart data={last90Sales}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip formatter={(v) => fmtLKR.format(v)} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="direct"
                    name="Direct"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="bonus"
                    name="Indirect"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Affiliate Sales Count — Direct vs Indirect (Last 90 days)">
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={affSalesCount90}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="directCount" name="Direct (L1)" />
                  <Bar dataKey="indirectCount" name="Indirect (L2+)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Instructor Referral — Daily Amount (Last 90 days)">
            <div className="h-56">
              <ResponsiveContainer>
                <LineChart data={instrRefLast90}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip formatter={(v) => fmtLKR.format(v)} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    name="Amount"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
  <Card title="Settlement Status">
    <div className="grid grid-cols-3 gap-3 mb-4">
      <MiniStat label="Submitted" value={fmtLKR.format(settlementSummary.submitted)} tone="amber" />
      <MiniStat label="Confirmed" value={fmtLKR.format(settlementSummary.confirmed)} tone="green" />
      <MiniStat label="Disputed" value={fmtLKR.format(settlementSummary.disputed)} tone="rose" />
    </div>
    <div className="space-y-3">
      {settlementSummary.rows.length === 0 ? (
        <div className="text-sm text-gray-400">No settlement records yet.</div>
      ) : (
        settlementSummary.rows.map((row) => (
          <div key={row.id} className="rounded-2xl border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-gray-800">
                  {row.instructorSnapshot?.name || row.instructorSnapshot?.slug || "Instructor"}
                </div>
                <div className="text-xs text-gray-500">
                  {row.monthKey} · {row.status || "submitted"}
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold">{fmtLKR.format(Number(row.totalCommission || 0))}</div>
                <div className="text-xs text-gray-500">{row.orderCount || 0} sales</div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </Card>

  <Card title="Settlement Notes">
    <div className="space-y-3 text-sm">
      <div className="rounded-2xl bg-white/70 border p-4">
        Submitted settlements are waiting for your confirmation.
      </div>
      <div className="rounded-2xl bg-white/70 border p-4">
        Confirmed settlements show money the instructor marked as paid and you accepted.
      </div>
      <div className="rounded-2xl bg-white/70 border p-4">
        Disputed settlements need follow-up with the instructor or admin.
      </div>
    </div>
  </Card>
</div>

        {/* ===== Tables ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Affiliate Sales Count table — last 30 days */}
          <Card title="Affiliate Sales Count — Daily (Last 30 days)">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-gray-500">
                  <tr>
                    <th className="py-2">Date</th>
                    <th className="py-2 text-right">Direct</th>
                    <th className="py-2 text-right">Indirect</th>
                    <th className="py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {affSalesCount90.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-4 text-gray-400">
                        No sales yet.
                      </td>
                    </tr>
                  )}
                  {affSalesCount90.slice(-30).map((r) => (
                    <tr key={r.date} className="hover:bg-gray-50">
                      <td className="py-2">{r.date}</td>
                      <td className="py-2 text-right">{r.directCount || 0}</td>
                      <td className="py-2 text-right">
                        {r.indirectCount || 0}
                      </td>
                      <td className="py-2 text-right font-semibold">
                        {(r.directCount || 0) + (r.indirectCount || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Instructor Referral — latest */}
          <Card title="Instructor Referral — Latest">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-gray-500">
                  <tr>
                    <th className="py-2">Date</th>
                    <th className="py-2">Source</th>
                    <th className="py-2 text-right">Your Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {instrRefRows.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-4 text-gray-400">
                        No referral earnings yet.
                      </td>
                    </tr>
                  )}
                  {instrRefRows.map((o) => {
                    const dt =
                      o.createdAt?.toDate?.() ||
                      new Date(o.createdAt || Date.now());
                    const sourceLabel =
                      o.packageTitle ||
                      o.itemTitle ||
                      o.courseTitle ||
                      o.productTitle ||
                      o.serviceTitle ||
                      o.sourceType ||
                      "Referral earning";
                    return (
                      <tr key={o.id} className="hover:bg-gray-50">
                        <td className="py-2">{dt.toLocaleDateString()}</td>
                        <td className="py-2 truncate">{sourceLabel}</td>
                        <td className="py-2 text-right font-semibold">
                          {fmtLKR.format(Number(o.amount || 0))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Existing affiliate tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <Card title="Direct Sales History (Affiliate)">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-gray-500">
                  <tr>
                    <th className="py-2">Date</th>
                    <th className="py-2">Item / Course</th>
                    <th className="py-2 text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {directSales.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-4 text-gray-400">
                        No direct sales yet.
                      </td>
                    </tr>
                  )}
                  {directSales.map((p) => {
                    const date = p.createdAt?.toDate
                      ? p.createdAt.toDate()
                      : new Date(p.createdAt);
                    const name =
                      p.courseTitle ||
                      p.productTitle ||
                      p.title ||
                      `Order #${String(p.orderId || p.id).slice(0, 6)}`;
                    const revenueAmt = Number(p.amount ?? 0);

                    return (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="py-2">{date.toLocaleDateString()}</td>
                        <td className="py-2">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full text-[11px] bg-blue-50 text-blue-700 capitalize">
                              {p.productType || "course"}
                            </span>
                            <span className="truncate">{name}</span>
                          </div>
                        </td>
                        <td className="py-2 text-right font-semibold">
                          {fmtLKR.format(revenueAmt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="Payment History (Affiliate)">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-2">Date</th>
                    <th className="py-2">Amount</th>
                    <th className="py-2">Status</th>
                    <th className="py-2">Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-4 text-gray-400">
                        No payouts yet.
                      </td>
                    </tr>
                  )}
                  {history.slice(0, 8).map((p) => (
                    <tr key={p.id} className="border-t">
                      <td className="py-2">
                        {p.createdAt?.toDate
                          ? p.createdAt.toDate().toLocaleDateString()
                          : new Date(p.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-2 font-semibold">
                        {fmtLKR.format(Number(p.amount || 0))}
                      </td>
                      <td className="py-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs ${
                            String(p.status || "").toLowerCase() === "paid"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {p.status || "pending"}
                        </span>
                      </td>
                      <td className="py-2 text-gray-500">
                        {(p.orderId || p.id || "").toString().slice(0, 8)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ---------- UI bits ---------- */
function MiniStat({ label, value, tone = "blue" }) {
  const toneMap = {
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    green: "bg-green-50 text-green-800 border-green-100",
    rose: "bg-rose-50 text-rose-800 border-rose-100",
    blue: "bg-blue-50 text-blue-800 border-blue-100",
  };
  return (
    <div className={`rounded-2xl border p-3 ${toneMap[tone] || toneMap.blue}`}>
      <div className="text-xs font-semibold uppercase tracking-wide">{label}</div>
      <div className="mt-2 text-sm font-bold">{value}</div>
    </div>
  );
}

function KPI({ color = "blue", title, value }) {
  const map = {
    blue: "bg-blue-50 text-blue-600",
    indigo: "bg-indigo-50 text-indigo-600",
    violet: "bg-violet-50 text-violet-600",
    green: "bg-green-50 text-green-600",
  };

  const displayValue = String(value ?? "—");
  const currencyMatch = displayValue.match(/^([A-Z]{3})\s+(.+)$/);

  return (
    <div
      className={`rounded-2xl p-6 shadow ${map[color]} flex min-h-[152px] flex-col items-center justify-center`}
    >
      {currencyMatch ? (
        <div className="min-w-0 text-center leading-tight">
          <div className="text-sm font-semibold uppercase tracking-[0.18em]">
            {currencyMatch[1]}
          </div>
          <div className="mt-1 break-words text-3xl font-bold leading-none md:text-[2.5rem]">
            {currencyMatch[2]}
          </div>
        </div>
      ) : (
        <span className="min-w-0 break-words text-center text-3xl font-bold leading-tight md:text-[2.5rem]">
          {displayValue}
        </span>
      )}
      <span className="mt-3 text-center text-gray-700">{title}</span>
    </div>
  );
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-white rounded-2xl shadow p-6 ${className}`}>
      <div className="text-lg font-bold mb-3">{title}</div>
      {children}
    </div>
  );
}
